import React, { useState, useEffect } from "react";
import { useExpenses } from "../../hooks/useExpenses";
import { useEmployees } from "../../hooks/useEmployees";
import ExpenseTable from "./ExpenseTable";
import ExpenseFormModal from "./ExpenseFormModal";
import LoadingSpinner from "../../../../Component/Shared/LoadingSpinner/LoadingSpinner";
import ConfirmModal from "../../../../Component/Shared/ConfirmModal/ConfirmModal";
import styles from "./ExpensesTab.module.css";

const EXPENSE_TYPES = [
  { value: "Rent", label: "إيجار" },
  { value: "Utilities", label: "مرافق" },
  { value: "Supplies", label: "مستلزمات" },
  { value: "Maintenance", label: "صيانة" },
  { value: "SalaryAndCommission", label: "رواتب وعمولات" },
  { value: "Other", label: "أخرى" },
];

const ExpensesTab = () => {
  const { employees } = useEmployees();
  const {
    expenses, loading, error, filters, setFilters,
    loadExpenses, addExpense, updateExpense, removeExpense,
  } = useExpenses();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const applyFilters = () => loadExpenses(filters);

  const resetFilters = () => {
    const cleared = { from: "", to: "", expenseType: "", employeeId: "" };
    setFilters(cleared);
    loadExpenses(cleared);
  };

  const openAddModal = () => { setEditingExpense(null); setModalOpen(true); };
  const openEditModal = (expense) => { setEditingExpense(expense); setModalOpen(true); };

  const handleSubmit = async (formData) => {
    if (editingExpense) await updateExpense(editingExpense.id, formData);
    else await addExpense(formData);
    setModalOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    await removeExpense(deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
  };

  const totalAmount = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <h2>قائمة المصروفات</h2>
        <button className={styles.addBtn} onClick={openAddModal}>+ إضافة مصروف</button>
      </div>

      <div className={styles.filtersBar}>
        <label>من تاريخ
          <input type="date" name="from" value={filters.from} onChange={handleFilterChange} />
        </label>
        <label>إلى تاريخ
          <input type="date" name="to" value={filters.to} onChange={handleFilterChange} />
        </label>
        <label>نوع المصروف
          <select name="expenseType" value={filters.expenseType} onChange={handleFilterChange}>
            <option value="">الكل</option>
            {EXPENSE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                    {type.label}
                </option>
            ))}

          </select>
        </label>
        <label>الموظف
          <select name="employeeId" value={filters.employeeId} onChange={handleFilterChange}>
            <option value="">الكل</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.fullName}</option>)}
          </select>
        </label>
        <div className={styles.filterActions}>
          <button className={styles.applyBtn} onClick={applyFilters}>تطبيق</button>
          <button className={styles.resetBtn} onClick={resetFilters}>إعادة تعيين</button>
        </div>
      </div>

      <div className={styles.totalBar}>
        إجمالي المصروفات المعروضة: <strong>{totalAmount.toLocaleString()} ج.م</strong>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <LoadingSpinner text="جاري تحميل المصروفات..." />
      ) : (
        <ExpenseTable expenses={expenses} onEdit={openEditModal} onDelete={setDeleteTarget} />
      )}

      {modalOpen && (
        <ExpenseFormModal
          initialData={editingExpense}
          employees={employees}
          expenseTypes={EXPENSE_TYPES}
          onSubmit={handleSubmit}
          onClose={() => setModalOpen(false)}
        />
      )}

      <ConfirmModal
        open={!!deleteTarget}
        title="حذف مصروف"
        message={`هل أنت متأكد من حذف "${deleteTarget?.title}"؟`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        loading={deleting}
      />
    </div>
  );
};

export default ExpensesTab;